// ============================================================
// GenreClassifier.js — Rule-based genre classification
// Maps audio analysis results (BPM, key, energy) to probable
// genres using weighted heuristic rules.
//
// Each genre has a "fingerprint" of typical characteristics.
// The classifier scores each genre against the analysis and
// returns a ranked list of matches with confidence scores.
// ============================================================

/**
 * Genre fingerprint definition.
 * Each genre specifies expected ranges for BPM, energy distribution,
 * percussive/melodic balance, and density. Fields not specified are "don't care."
 *
 * @typedef {Object} GenreFingerprint
 * @property {string} name - Display name
 * @property {string} family - Parent family (electronic, hip-hop, rock, etc.)
 * @property {[number, number]} bpmRange - [min, max] BPM
 * @property {[number, number]} [bassRange] - Expected bass level [min, max] (0-1)
 * @property {[number, number]} [midRange] - Expected mid level
 * @property {[number, number]} [trebleRange] - Expected treble level
 * @property {[number, number]} [percussiveRange] - Expected percussive score
 * @property {[number, number]} [melodicRange] - Expected melodic score
 * @property {string[]} [densityMatch] - Expected density labels
 * @property {number} weight - Relative importance (higher = stricter match required)
 */

const GENRE_FINGERPRINTS = [
  // === ELECTRONIC / DANCE ===
  {
    name: 'Drum & Bass',
    family: 'electronic',
    bpmRange: [165, 185],
    bassRange: [0.6, 1.0],
    midRange: [0.1, 0.6],
    trebleRange: [0.05, 0.4],
    percussiveRange: [0.3, 1.0],
    densityMatch: ['bass-heavy', 'dense', 'percussive'],
    weight: 1.0
  },
  {
    name: 'Liquid DnB',
    family: 'electronic',
    bpmRange: [165, 180],
    bassRange: [0.4, 0.85],
    midRange: [0.3, 0.7],
    trebleRange: [0.1, 0.5],
    percussiveRange: [0.15, 0.5],
    melodicRange: [0.4, 1.0],
    densityMatch: ['balanced', 'melodic'],
    weight: 1.0
  },
  {
    name: 'Neurofunk',
    family: 'electronic',
    bpmRange: [170, 180],
    bassRange: [0.7, 1.0],
    midRange: [0.3, 0.7],
    trebleRange: [0.15, 0.5],
    percussiveRange: [0.4, 0.9],
    melodicRange: [0.1, 0.4],
    densityMatch: ['bass-heavy', 'dense'],
    weight: 1.0
  },
  {
    name: 'Bass House',
    family: 'electronic',
    bpmRange: [124, 132],
    bassRange: [0.6, 1.0],
    midRange: [0.2, 0.6],
    trebleRange: [0.1, 0.4],
    percussiveRange: [0.3, 0.7],
    densityMatch: ['bass-heavy', 'dense'],
    weight: 1.0
  },
  {
    name: 'Deep House',
    family: 'electronic',
    bpmRange: [118, 128],
    bassRange: [0.5, 0.9],
    midRange: [0.2, 0.5],
    trebleRange: [0.1, 0.4],
    percussiveRange: [0.2, 0.5],
    melodicRange: [0.3, 0.7],
    densityMatch: ['bass-heavy', 'balanced'],
    weight: 1.0
  },
  {
    name: 'Tech House',
    family: 'electronic',
    bpmRange: [122, 130],
    bassRange: [0.5, 0.85],
    midRange: [0.25, 0.6],
    trebleRange: [0.15, 0.5],
    percussiveRange: [0.4, 0.8],
    melodicRange: [0.1, 0.4],
    densityMatch: ['percussive', 'balanced'],
    weight: 1.0
  },
  {
    name: 'Techno',
    family: 'electronic',
    bpmRange: [128, 145],
    bassRange: [0.5, 0.95],
    midRange: [0.15, 0.5],
    trebleRange: [0.1, 0.5],
    percussiveRange: [0.5, 1.0],
    melodicRange: [0.0, 0.3],
    densityMatch: ['percussive', 'bass-heavy', 'dense'],
    weight: 1.0
  },
  {
    name: 'Melodic Techno',
    family: 'electronic',
    bpmRange: [120, 135],
    bassRange: [0.4, 0.8],
    midRange: [0.3, 0.7],
    trebleRange: [0.1, 0.5],
    percussiveRange: [0.3, 0.6],
    melodicRange: [0.4, 0.9],
    densityMatch: ['balanced', 'melodic'],
    weight: 1.0
  },
  {
    name: 'Trance',
    family: 'electronic',
    bpmRange: [128, 145],
    bassRange: [0.4, 0.8],
    midRange: [0.4, 0.8],
    trebleRange: [0.2, 0.6],
    percussiveRange: [0.2, 0.5],
    melodicRange: [0.5, 1.0],
    densityMatch: ['balanced', 'melodic', 'bright'],
    weight: 1.0
  },
  {
    name: 'Progressive House',
    family: 'electronic',
    bpmRange: [122, 132],
    bassRange: [0.4, 0.75],
    midRange: [0.3, 0.7],
    trebleRange: [0.15, 0.5],
    percussiveRange: [0.2, 0.5],
    melodicRange: [0.3, 0.8],
    densityMatch: ['balanced', 'melodic'],
    weight: 1.0
  },
  {
    name: 'Dubstep',
    family: 'electronic',
    bpmRange: [138, 142],
    bassRange: [0.7, 1.0],
    midRange: [0.2, 0.7],
    trebleRange: [0.1, 0.5],
    percussiveRange: [0.4, 0.9],
    densityMatch: ['bass-heavy', 'dense'],
    weight: 1.0
  },
  {
    name: 'Riddim',
    family: 'electronic',
    bpmRange: [138, 155],
    bassRange: [0.8, 1.0],
    midRange: [0.15, 0.5],
    trebleRange: [0.05, 0.3],
    percussiveRange: [0.5, 1.0],
    densityMatch: ['bass-heavy', 'percussive'],
    weight: 1.0
  },
  {
    name: 'Garage / UK Garage',
    family: 'electronic',
    bpmRange: [128, 135],
    bassRange: [0.5, 0.85],
    midRange: [0.3, 0.6],
    trebleRange: [0.15, 0.5],
    percussiveRange: [0.3, 0.6],
    melodicRange: [0.3, 0.7],
    densityMatch: ['balanced', 'bass-heavy'],
    weight: 1.0
  },
  {
    name: 'Jungle',
    family: 'electronic',
    bpmRange: [155, 175],
    bassRange: [0.6, 1.0],
    midRange: [0.15, 0.5],
    trebleRange: [0.1, 0.4],
    percussiveRange: [0.5, 1.0],
    densityMatch: ['bass-heavy', 'percussive'],
    weight: 1.0
  },
  {
    name: 'Electro House',
    family: 'electronic',
    bpmRange: [126, 132],
    bassRange: [0.5, 0.9],
    midRange: [0.3, 0.7],
    trebleRange: [0.2, 0.6],
    percussiveRange: [0.3, 0.7],
    densityMatch: ['dense', 'bass-heavy'],
    weight: 1.0
  },
  {
    name: 'Future Bass',
    family: 'electronic',
    bpmRange: [140, 170],
    bassRange: [0.5, 0.9],
    midRange: [0.4, 0.8],
    trebleRange: [0.2, 0.6],
    percussiveRange: [0.2, 0.5],
    melodicRange: [0.5, 1.0],
    densityMatch: ['balanced', 'melodic', 'bright'],
    weight: 1.0
  },
  {
    name: 'Hardstyle',
    family: 'electronic',
    bpmRange: [148, 160],
    bassRange: [0.7, 1.0],
    midRange: [0.2, 0.6],
    trebleRange: [0.1, 0.4],
    percussiveRange: [0.6, 1.0],
    densityMatch: ['bass-heavy', 'dense', 'percussive'],
    weight: 1.0
  },
  {
    name: 'Ambient',
    family: 'electronic',
    bpmRange: [60, 120],
    bassRange: [0.1, 0.5],
    midRange: [0.2, 0.7],
    trebleRange: [0.2, 0.7],
    percussiveRange: [0.0, 0.15],
    melodicRange: [0.4, 1.0],
    densityMatch: ['sparse', 'balanced', 'melodic'],
    weight: 1.0
  },
  {
    name: 'Downtempo',
    family: 'electronic',
    bpmRange: [80, 115],
    bassRange: [0.3, 0.7],
    midRange: [0.3, 0.7],
    trebleRange: [0.1, 0.5],
    percussiveRange: [0.1, 0.4],
    melodicRange: [0.3, 0.8],
    densityMatch: ['balanced', 'sparse', 'melodic'],
    weight: 1.0
  },

  // === HIP-HOP / RAP ===
  {
    name: 'Boom Bap',
    family: 'hip-hop',
    bpmRange: [85, 100],
    bassRange: [0.5, 0.9],
    midRange: [0.2, 0.6],
    trebleRange: [0.1, 0.4],
    percussiveRange: [0.5, 0.9],
    densityMatch: ['bass-heavy', 'percussive'],
    weight: 1.0
  },
  {
    name: 'Trap',
    family: 'hip-hop',
    bpmRange: [130, 170],
    bassRange: [0.7, 1.0],
    midRange: [0.1, 0.5],
    trebleRange: [0.15, 0.5],
    percussiveRange: [0.4, 0.9],
    densityMatch: ['bass-heavy', 'dense'],
    weight: 1.0
  },
  {
    name: 'Lo-Fi Hip Hop',
    family: 'hip-hop',
    bpmRange: [70, 95],
    bassRange: [0.3, 0.7],
    midRange: [0.3, 0.7],
    trebleRange: [0.05, 0.3],
    percussiveRange: [0.2, 0.5],
    melodicRange: [0.4, 0.9],
    densityMatch: ['balanced', 'sparse'],
    weight: 1.0
  },
  {
    name: 'Drill',
    family: 'hip-hop',
    bpmRange: [138, 145],
    bassRange: [0.6, 1.0],
    midRange: [0.15, 0.5],
    trebleRange: [0.1, 0.4],
    percussiveRange: [0.5, 1.0],
    densityMatch: ['bass-heavy', 'percussive'],
    weight: 1.0
  },

  // === POP / R&B ===
  {
    name: 'Pop',
    family: 'pop',
    bpmRange: [100, 130],
    bassRange: [0.3, 0.7],
    midRange: [0.4, 0.8],
    trebleRange: [0.2, 0.6],
    percussiveRange: [0.2, 0.5],
    melodicRange: [0.5, 1.0],
    densityMatch: ['balanced', 'bright', 'melodic'],
    weight: 0.9
  },
  {
    name: 'R&B',
    family: 'pop',
    bpmRange: [70, 110],
    bassRange: [0.4, 0.8],
    midRange: [0.3, 0.7],
    trebleRange: [0.1, 0.4],
    percussiveRange: [0.15, 0.4],
    melodicRange: [0.5, 1.0],
    densityMatch: ['balanced', 'bass-heavy', 'melodic'],
    weight: 0.9
  },

  // === ROCK / METAL ===
  {
    name: 'Rock',
    family: 'rock',
    bpmRange: [100, 150],
    bassRange: [0.4, 0.8],
    midRange: [0.5, 0.9],
    trebleRange: [0.3, 0.7],
    percussiveRange: [0.3, 0.7],
    densityMatch: ['balanced', 'dense'],
    weight: 0.9
  },
  {
    name: 'Punk Rock',
    family: 'rock',
    bpmRange: [150, 200],
    bassRange: [0.4, 0.8],
    midRange: [0.5, 0.9],
    trebleRange: [0.3, 0.7],
    percussiveRange: [0.5, 0.9],
    densityMatch: ['dense', 'bright'],
    weight: 0.9
  },
  {
    name: 'Metal',
    family: 'rock',
    bpmRange: [120, 200],
    bassRange: [0.5, 0.9],
    midRange: [0.5, 0.9],
    trebleRange: [0.3, 0.7],
    percussiveRange: [0.6, 1.0],
    densityMatch: ['dense', 'percussive'],
    weight: 0.9
  },
  {
    name: 'Melodic Hardcore',
    family: 'rock',
    bpmRange: [140, 190],
    bassRange: [0.5, 0.9],
    midRange: [0.5, 0.9],
    trebleRange: [0.2, 0.6],
    percussiveRange: [0.4, 0.8],
    melodicRange: [0.3, 0.7],
    densityMatch: ['dense', 'balanced'],
    weight: 1.0
  },
];

/**
 * Score how well an analysis result matches a genre fingerprint.
 * Returns a value from 0 (no match) to 1 (perfect match).
 *
 * Each dimension (BPM, bass, mid, etc.) contributes a partial score.
 * If a value falls within the expected range, it scores 1.0 for that dimension.
 * If it falls outside, the score decays based on distance from range edges.
 *
 * @param {Object} analysis - Result from analyzeLoop()
 * @param {GenreFingerprint} fingerprint
 * @returns {number} Match score 0-1
 */
function scoreGenreMatch(analysis, fingerprint) {
  const scores = [];
  const weights = [];

  // Helper: score a value against a [min, max] range
  // Returns 1.0 if in range, decays smoothly outside
  const rangeScore = (value, range) => {
    if (!range) return null; // "don't care" — skip this dimension
    const [min, max] = range;
    if (value >= min && value <= max) return 1.0;
    // Distance from nearest edge, normalized by range width
    const rangeWidth = max - min || 0.1;
    const dist = value < min ? min - value : value - max;
    return Math.max(0, 1 - (dist / rangeWidth) * 2);
  };

  // BPM — most important dimension (weight: 3)
  const bpmScore = rangeScore(analysis.bpm, fingerprint.bpmRange);
  if (bpmScore !== null) {
    scores.push(bpmScore);
    weights.push(3);
  }

  // Energy bands (weight: 2 each)
  const energy = analysis.energy;
  for (const [key, range, w] of [
    ['bass', fingerprint.bassRange, 2],
    ['mid', fingerprint.midRange, 1.5],
    ['treble', fingerprint.trebleRange, 1],
  ]) {
    const s = rangeScore(energy[key], range);
    if (s !== null) { scores.push(s); weights.push(w); }
  }

  // Percussive / melodic (weight: 1.5)
  const percS = rangeScore(energy.percussive, fingerprint.percussiveRange);
  if (percS !== null) { scores.push(percS); weights.push(1.5); }

  const melS = rangeScore(energy.melodic, fingerprint.melodicRange);
  if (melS !== null) { scores.push(melS); weights.push(1.5); }

  // Density label match (weight: 1)
  if (fingerprint.densityMatch && fingerprint.densityMatch.length > 0) {
    const densityScore = fingerprint.densityMatch.includes(energy.density) ? 1.0 : 0.3;
    scores.push(densityScore);
    weights.push(1);
  }

  if (scores.length === 0) return 0;

  // Weighted average
  let totalWeight = 0, totalScore = 0;
  for (let i = 0; i < scores.length; i++) {
    totalScore += scores[i] * weights[i];
    totalWeight += weights[i];
  }

  return (totalScore / totalWeight) * (fingerprint.weight || 1);
}

/**
 * Classify the genre of an analyzed loop.
 * Returns a ranked list of genre matches with confidence scores.
 *
 * @param {Object} analysis - Result from analyzeLoop()
 * @returns {{
 *   primary: { name: string, family: string, confidence: number },
 *   alternatives: Array<{ name: string, family: string, confidence: number }>,
 *   family: string
 * }}
 */
export function classifyGenre(analysis) {
  const results = GENRE_FINGERPRINTS.map(fp => ({
    name: fp.name,
    family: fp.family,
    confidence: Math.round(scoreGenreMatch(analysis, fp) * 100) / 100
  }));

  // Sort by confidence descending
  results.sort((a, b) => b.confidence - a.confidence);

  const primary = results[0];
  // Alternatives: genuine runners-up only. Must be above 0.5 confidence AND
  // strictly below the primary — a "similar to" entry that ties or beats the
  // winner reads as a broken/arbitrary score to a musician. Cap at 3.
  const alternatives = results
    .slice(1)
    .filter(r => r.confidence > 0.5 && r.confidence < primary.confidence)
    .slice(0, 3);

  return {
    primary,
    alternatives,
    family: primary.family
  };
}

/**
 * Get the full list of supported genres (for UI display).
 * @returns {Array<{name: string, family: string}>}
 */
export function getSupportedGenres() {
  return GENRE_FINGERPRINTS.map(fp => ({
    name: fp.name,
    family: fp.family
  }));
}

/**
 * Get genre fingerprint by name (for arrangement generation).
 * @param {string} name
 * @returns {GenreFingerprint|null}
 */
export function getGenreFingerprint(name) {
  return GENRE_FINGERPRINTS.find(fp =>
    fp.name.toLowerCase() === name.toLowerCase()
  ) || null;
}
